import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// #41: Screenshots liegen als data-URLs in der Datenbank. Ein <a href="data:…" target="_blank">
// öffnet in Chrome seit v60 einen leeren Tab (Anti-Phishing-Sperre gegen Top-Level-Navigation
// zu data:-URLs) — die Vergrößerung muss deshalb INNERHALB der App passieren.
// Bewusst inhaltsfrei gehalten (nur Bildliste + Startindex + Beschriftung), damit Lauf-Historie,
// Defect-Detail und Ausführungs-Panel dieselbe Komponente nutzen statt drei eigene Overlays.

// Review #41: `startIndex` kommt vom Aufrufer und ist nicht vertrauenswürdig — der naheliegende
// Fall ist `images.indexOf(x)`, das bei einem Treffer-losen Suchbild `-1` liefert. Ohne Klemmung
// nach unten würde dieser Wert unverändert zum State und damit an jede Stelle durchgereicht, die
// `index` liest (Bild, Zähler, Button-disabled-Zustand). Deshalb wird hier an der Quelle geklemmt
// (State-Initialwert), nicht erst an der Anzeigestelle — sonst wäre zwar das Bild korrekt, Zähler
// und Blätter-Buttons aber weiterhin auf Basis des unklemmten Index falsch.
function clampImageIndex(index: number, length: number) {
  return Math.max(0, Math.min(index, length - 1));
}

export function ImageLightbox({
  images,
  startIndex,
  label,
  onClose,
}: {
  images: string[];
  startIndex: number;
  label: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  // Achtung für Aufrufer: Der Initialwert wird nur beim Mounten gelesen. Bleibt dieselbe Instanz
  // stehen und wechselt nur `startIndex` bzw. `images`, zeigt das Overlay weiter das alte Bild.
  // Wer es für mehrere Datensätze wiederverwendet, braucht deshalb ein `key` auf der
  // Inhalts-Identität (z. B. der Lauf-ID) — dieselbe Falle wie beim Defect-Sidepanel.
  const [index, setIndex] = useState(() => clampImageIndex(startIndex, images.length));
  const dialogRef = useRef<HTMLDialogElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // Ref statt Effekt-Abhängigkeit: der ArrowRight-Handler unten braucht die AKTUELLE Länge, ohne
  // dass der Tastatur-Effekt bei jeder Bildzahl-Änderung neu montiert wird (Branch-Review, kleiner
  // Punkt) — ein Remount hätte sonst bei jeder Längenänderung den Fokus erneut ins Overlay
  // gerissen, weil die Fokus-Übernahme frueher im selben Effekt lag (siehe Fokus-Effekt unten, der
  // deshalb bewusst getrennt und nur einmal pro Mount läuft). `useRef`-Objekte sind für den
  // React-Hooks-Lint stabil und müssen nicht in die Abhängigkeitsliste.
  const imagesLengthRef = useRef(images.length);
  imagesLengthRef.current = images.length;

  // Hooks stehen vor dem frühen `return null` unten — sonst verletzt ein Aufruf mit leerem
  // `images`-Array die Regeln der Hooks (unterschiedliche Hook-Anzahl zwischen Renders).

  // Fokus-Übernahme + Fokus-Rückgabe (Spec §3.2/§3.5, Branch-Review Befund 5): getrennt vom
  // Tastatur-Effekt und mit leerer Abhängigkeitsliste, läuft also genau einmal beim Mounten und
  // einmal beim Unmounten. Das auslösende Element (typischerweise der Thumbnail-Button) wird VOR
  // dem Fokus-Wechsel ins Overlay gemerkt, sonst wäre `document.activeElement` zu diesem Zeitpunkt
  // bereits das Dialog-Element selbst.
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    return () => {
      // contains-Check: das ausloesende Element kann inzwischen aus dem DOM verschwunden sein
      // (z. B. der Screenshot wurde entfernt, waehrend das Overlay noch offen war) — ein Aufruf
      // von focus() auf einem entfernten Knoten wirft zwar nicht, haette aber keine Wirkung; ohne
      // Check faellt der Fokus dann still auf <body>, was den Tastaturnutzer trotzdem verliert.
      if (previouslyFocused && document.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
    };
  }, []);

  // Tastatur-Bedienung + Fokusfalle. Capture-Phase UND stopPropagation auf JEDER hier
  // beanspruchten Taste (Branch-Review Befund 1+2): dieses Overlay ist ein modales `<dialog>` und
  // darf keine Taste nach unten durchreichen. Betroffen waren zwei Fehlerklassen im Zusammenspiel
  // mit anderen Komponenten, die selbst document-Listener registrieren:
  //   - Enter wertet die Lightbox nicht aus, muss es aber trotzdem abfangen — sonst laeuft es zum
  //     window-Listener des Ausfuehrungs-Panels durch und bucht den Testlauf als "bestanden", waehrend
  //     der Tester nur ein Bild betrachten wollte (Befund 1).
  //   - Escape wuerde sonst zugleich das SidePanel schliessen, in dem die Lauf-Historie (und damit
  //     die Lightbox) haeufig eingebettet ist (Befund 2).
  // stopPropagation() (nicht stopImmediatePropagation()) genuegt: Capture-Listener auf `document`
  // laufen bei JEDEM Dispatch vollstaendig VOR den Bubble-Listenern desselben Knotens (Capture-
  // Phase ist fuer den gesamten Pfad abgeschlossen, bevor die Bubble-Phase beginnt) — das gilt
  // unabhaengig von der Registrierungsreihenfolge. stopPropagation() an dieser Stelle bricht den
  // Dispatch deshalb VOR dem Bubble-Listener von side-panel.tsx (document, Bubble) und VOR dem
  // window-Listener von execution-panel.tsx (window, Bubble, noch weiter aussen) ab. Belegt durch
  // die Tests "Enter loest waehrend offener Lightbox nicht 'Passt' aus" (execution-panel.test.tsx)
  // und "Escape schliesst nur die Lightbox, nicht das SidePanel darunter" (plan-progress.test.tsx) —
  // beide liefen rot, solange hier Bubble-Phase ohne stopPropagation stand, und gruen mit dieser
  // Kombination; stopImmediatePropagation() war in keinem der beiden Laeufe noetig.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key === 'ArrowRight') {
        e.stopPropagation();
        setIndex((i) => Math.min(i + 1, imagesLengthRef.current - 1));
        return;
      }
      if (e.key === 'ArrowLeft') {
        e.stopPropagation();
        setIndex((i) => Math.max(i - 1, 0));
        return;
      }
      if (e.key === 'Enter') {
        e.stopPropagation();
        return;
      }
      if (e.key === 'Tab') {
        // Fokusfalle (Befund 5): die einzigen fokussierbaren Elemente im Overlay sind seine
        // <button>-Kinder (Backdrop, ✕, ‹, ›) — sie stehen bereits in genau dieser Reihenfolge im
        // DOM, natives Tab/Shift+Tab reicht also fuer die Bewegung DAZWISCHEN. Nur an den beiden
        // Enden wird eingegriffen, damit Tab zyklisch bleibt statt aus dem Overlay hinauszulaufen.
        e.stopPropagation();
        const container = containerRef.current;
        if (!container) return;
        const focusables = Array.from(container.querySelectorAll<HTMLButtonElement>('button'));
        if (focusables.length === 0) return;
        const first = focusables[0] as HTMLButtonElement;
        const last = focusables[focusables.length - 1] as HTMLButtonElement;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  if (images.length === 0) return null;
  // Einmal klemmen, dann überall denselben Wert benutzen: Verkürzt sich `images` nach dem Mount,
  // wäre sonst das Bild korrekt, Zähler und Blätter-Buttons aber weiterhin auf Basis des rohen
  // Index falsch (etwa "101 von 3").
  const safeIndex = clampImageIndex(index, images.length);
  const current = images[safeIndex];
  const many = images.length > 1;

  return (
    <div ref={containerRef} className="fixed inset-0 z-50 flex items-center justify-center">
      <button
        type="button"
        aria-label={t('common.close')}
        onClick={onClose}
        className="absolute inset-0 bg-black/70"
      />
      {/* Natives <dialog open> wie im SidePanel: role=dialog kommt implizit, und `open` statt
          showModal() hält unseren eigenen Backdrop-Button klickbar. */}
      <dialog
        ref={dialogRef}
        open
        tabIndex={-1}
        aria-modal="true"
        aria-label={label}
        className="relative z-10 m-0 flex max-h-[90vh] max-w-[92vw] flex-col gap-2 rounded-card border border-[var(--border)] bg-bg-surface p-3 text-text-body shadow-xl outline-none"
      >
        <div className="flex items-center justify-between gap-4">
          {/* Beide nebeneinander statt Zähler-ersetzt-Label (Branch-Review, kleiner Punkt): der
              Kontext (welches Bild/welcher Lauf) verschwindet sonst genau dann, wenn es mehrere
              Bilder gibt — dabei ist das der haeufigere Fall. */}
          <span className="flex min-w-0 items-center gap-2 text-xs text-text-muted">
            <span className="truncate">{label}</span>
            {many && (
              <span className="shrink-0">
                {t('lightbox.counter', { current: safeIndex + 1, total: images.length })}
              </span>
            )}
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="rounded-btn px-2 py-1 text-text-muted hover:text-text-primary"
          >
            ✕
          </button>
        </div>
        <img src={current} alt={label} className="max-h-[75vh] max-w-full object-contain" />
        {many && (
          <div className="flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => setIndex((i) => Math.max(i - 1, 0))}
              disabled={safeIndex === 0}
              aria-label={t('lightbox.previous')}
              className="rounded-btn border border-[var(--border)] px-3 py-1 text-sm text-text-body disabled:opacity-40"
            >
              ‹
            </button>
            <button
              type="button"
              onClick={() => setIndex((i) => Math.min(i + 1, images.length - 1))}
              disabled={safeIndex === images.length - 1}
              aria-label={t('lightbox.next')}
              className="rounded-btn border border-[var(--border)] px-3 py-1 text-sm text-text-body disabled:opacity-40"
            >
              ›
            </button>
          </div>
        )}
      </dialog>
    </div>
  );
}
